import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { SVG_ICONS } from "./TablerIconPicker";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import OriMetricsReport from "./OriMetricsReport";
import BillingReport from "./BillingReport";
import SharingReport from "./SharingReport";
import ConsultationActivityReport from "./ConsultationActivityReport";
import "../styles/ListTab.css";

const ReportsTab = forwardRef((props, ref) => {
  const [menuVisible, setMenuVisible] = useState(() => {
    const saved = localStorage.getItem("reportsMenuVisible");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [activeReport, setActiveReport] = useState("content-interaction");
  const [timeRange, setTimeRange] = useState(30); // 7, 30, 60, or 90 days
  const [chartExpanded, setChartExpanded] = useState(false);
  const [metrics, setMetrics] = useState({
    newsClicks: 0,
    resourceClicks: 0,
    lessonsCompleted: 0,
    promptsCopied: 0,
    assessmentsTaken: 0,
    oriUsage: 0,
    loading: true
  });
  const [chartData, setChartData] = useState([]);
  const [topItems, setTopItems] = useState({
    news: [],
    resources: [],
    lessons: [],
    prompts: [],
    assessments: [],
    ori: []
  });
  const [topCategories, setTopCategories] = useState({
    resources: [],
    lessons: [],
    prompts: []
  });

  useEffect(() => {
    localStorage.setItem("reportsMenuVisible", JSON.stringify(menuVisible));
  }, [menuVisible]);

  useEffect(() => {
    loadMetrics();
  }, []);

  useImperativeHandle(ref, () => ({
    refreshData: loadMetrics
  }), [timeRange]);

  const loadMetrics = async () => {
    try {
      setMetrics(prev => ({ ...prev, loading: true }));

      // Get counts from all main tracking tables
      const newsClicksData = await sbFetch("GET", "news_clicks?select=id").catch(() => []);
      const resourceClicksData = await sbFetch("GET", "resource_clicks?select=id").catch(() => []);
      const lessonsData = await sbFetch("GET", "lessons_completed?select=id").catch(() => []);
      const promptsData = await sbFetch("GET", "prompt_copies?select=id").catch(() => []);
      const assessmentsData = await sbFetch("GET", "assessment_responses?select=id").catch(() => []);
      const oriUsageData = await sbFetch("GET", "ori_usage?select=id").catch(() => []);

      const newsClicks = Array.isArray(newsClicksData) ? newsClicksData.length : 0;
      const resourceClicks = Array.isArray(resourceClicksData) ? resourceClicksData.length : 0;
      const lessonsCompleted = Array.isArray(lessonsData) ? lessonsData.length : 0;
      const promptsCopied = Array.isArray(promptsData) ? promptsData.length : 0;
      const assessmentsTaken = Array.isArray(assessmentsData) ? assessmentsData.length : 0;
      const oriUsage = Array.isArray(oriUsageData) ? oriUsageData.length : 0;

      setMetrics({
        newsClicks,
        resourceClicks,
        lessonsCompleted,
        promptsCopied,
        assessmentsTaken,
        oriUsage,
        loading: false
      });

      // Load chart data with dates
      await loadChartData(timeRange);
    } catch (err) {
      console.error("Error loading metrics:", err);
      setMetrics(prev => ({ ...prev, loading: false }));
    }
  };

  const loadChartData = async (days) => {
    try {
      // Fetch data with timestamps - from all main tracking tables
      const [newsClicks, resourceClicks, lessons, prompts, assessments, oriUsage] = await Promise.all([
        sbFetch("GET", "news_clicks").catch(() => []),
        sbFetch("GET", "resource_clicks").catch(() => []),
        sbFetch("GET", "lessons_completed").catch(() => []),
        sbFetch("GET", "prompt_copies").catch(() => []),
        sbFetch("GET", "assessment_responses").catch(() => []),
        sbFetch("GET", "ori_usage").catch(() => [])
      ]);

      // Ensure all data is arrays
      const ensureArray = (data) => Array.isArray(data) ? data : [];
      const safeNewsClicks = ensureArray(newsClicks);
      const safeResourceClicks = ensureArray(resourceClicks);
      const safeLessons = ensureArray(lessons);
      const safePrompts = ensureArray(prompts);
      const safeAssessments = ensureArray(assessments);
      const safeOriUsage = ensureArray(oriUsage);

      // Create date-based map
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - days);

      const dateMap = {};

      // Create entries for each day in the range
      for (let i = 0; i < days; i++) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        const dateStr = d.toLocaleDateString();
        dateMap[dateStr] = { date: dateStr, news: 0, resources: 0, lessons: 0, prompts: 0, assessments: 0, ori: 0 };
      }

      // Helper to extract and aggregate by date
      const aggregateByDate = (items, dateField, key) => {
        if (!Array.isArray(items)) {
          console.error(`${key}: items is not an array`, items);
          return;
        }

        items.forEach(item => {
          const dateVal = item[dateField];
          if (!dateVal) return;
          const dateStr = new Date(dateVal).toLocaleDateString();
          if (dateMap[dateStr]) {
            dateMap[dateStr][key]++;
          }
        });
      };

      // Aggregate data from each source - using correct field names from schema
      aggregateByDate(safeNewsClicks, 'clicked_at', 'news');
      aggregateByDate(safeResourceClicks, 'clicked_at', 'resources');
      aggregateByDate(safeLessons, 'completed_at', 'lessons');
      aggregateByDate(safePrompts, 'copied_at', 'prompts');
      aggregateByDate(safeAssessments, 'createdat', 'assessments');
      aggregateByDate(safeOriUsage, 'sent_at', 'ori');

      // Sort by date and return
      const chart = Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
      setChartData(chart);
    } catch (err) {
      console.error("Error loading chart data:", err);
    }
  };

  useEffect(() => {
    loadChartData(timeRange);
    loadTopItems();
  }, [timeRange]);

  const loadTopItems = async () => {
    try {
      // Get all data without date filter for top items - all main tracking tables
      const [newsClicks, resources, lessons, prompts, assessments, oriUsageData] = await Promise.all([
        sbFetch("GET", "news_clicks").catch(() => []),
        sbFetch("GET", "resource_clicks").catch(() => []),
        sbFetch("GET", "lessons_completed").catch(() => []),
        sbFetch("GET", "prompt_copies").catch(() => []),
        sbFetch("GET", "assessment_responses").catch(() => []),
        sbFetch("GET", "ori_usage").catch(() => [])
      ]);

      // Count and sort each type - use correct field names
      const countByField = (items, idField, titleField) => {
        if (!Array.isArray(items) || items.length === 0) return [];

        const counts = {};
        items.forEach(item => {
          const idValue = item[idField];
          const titleValue = item[titleField];

          if (idValue) {
            const key = JSON.stringify(idValue);
            if (!counts[key]) counts[key] = { id: idValue, title: titleValue || 'Untitled', count: 0 };
            counts[key].count++;
          }
        });

        return Object.values(counts)
          .sort((a, b) => b.count - a.count)
          .slice(0, 3);
      };

      // For assessments, get names from app_settings
      const assessmentTitles = {};
      if (Array.isArray(assessments) && assessments.length > 0) {
        // Get all assessment settings to map IDs to names
        const settings = await sbFetch("GET", "app_settings");
        if (Array.isArray(settings)) {
          settings.forEach(setting => {
            try {
              const value = JSON.parse(setting.value);
              if (Array.isArray(value)) {
                value.forEach(assessment => {
                  if (assessment.id) {
                    assessmentTitles[assessment.id] = assessment.name || assessment.title || assessment.id;
                  }
                });
              }
            } catch (e) {
              // Skip invalid JSON
            }
          });
        }

        // Count assessments with proper names
        const counts = {};
        assessments.forEach(item => {
          const type = item.assessmenttype;
          if (type) {
            const key = JSON.stringify(type);
            const title = assessmentTitles[type] || type;
            if (!counts[key]) counts[key] = { id: type, title, count: 0 };
            counts[key].count++;
          }
        });

        var assessmentsTop = Object.values(counts)
          .sort((a, b) => b.count - a.count)
          .slice(0, 3);
      }

      // Count ORI topics
      const oriTopics = {};
      if (Array.isArray(oriUsageData)) {
        oriUsageData.forEach(item => {
          const topic = item.topic;
          if (topic) {
            const key = JSON.stringify(topic);
            if (!oriTopics[key]) oriTopics[key] = { id: topic, title: topic, count: 0 };
            oriTopics[key].count++;
          }
        });
      }
      const oriTop = Object.values(oriTopics)
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);

      // Fetch actual content to get categories
      const [promptsContent, , resourcesContent, lessonsContent] = await Promise.all([
        sbFetch("GET", "app_settings?key=eq.origamic-prompts").catch(() => []),
        sbFetch("GET", "app_settings?key=eq.origamic-assessments").catch(() => []),
        sbFetch("GET", "app_settings?key=eq.origamic-resources").catch(() => []),
        sbFetch("GET", "app_settings?key=eq.origamic-lessons").catch(() => [])
      ]);

      // Count categories
      const countCategories = (items, categoryField) => {
        if (!Array.isArray(items) || items.length === 0) return [];
        const counts = {};
        items.forEach(item => {
          const category = item[categoryField];
          if (category) {
            const key = JSON.stringify(category);
            if (!counts[key]) counts[key] = { category, count: 0 };
            counts[key].count++;
          }
        });
        return Object.values(counts)
          .sort((a, b) => b.count - a.count)
          .slice(0, 3);
      };

      // Process prompts categories
      let promptsList = [];
      if (Array.isArray(promptsContent) && promptsContent.length > 0) {
        try {
          promptsList = JSON.parse(promptsContent[0].value) || [];
        } catch (e) {
          promptsList = [];
        }
      }

      // Process resources categories
      let resourcesList = [];
      if (Array.isArray(resourcesContent) && resourcesContent.length > 0) {
        try {
          resourcesList = JSON.parse(resourcesContent[0].value) || [];
        } catch (e) {
          resourcesList = [];
        }
      }

      // Process lessons categories
      let lessonsList = [];
      if (Array.isArray(lessonsContent) && lessonsContent.length > 0) {
        try {
          lessonsList = JSON.parse(lessonsContent[0].value) || [];
        } catch (e) {
          lessonsList = [];
        }
      }

      setTopItems({
        news: countByField(newsClicks, 'news_id', 'title'),
        resources: countByField(resources, 'resource_id', 'resource_title'),
        lessons: countByField(lessons, 'lesson_id', 'lesson_title'),
        prompts: countByField(prompts, 'prompt_id', 'prompt_title'),
        assessments: assessmentsTop || [],
        ori: oriTop
      });

      setTopCategories({
        resources: countCategories(resourcesList, 'category'),
        lessons: countCategories(lessonsList, 'tag'),
        prompts: countCategories(promptsList, 'cat')
      });
    } catch (err) {
      console.error("Error loading top items:", err);
    }
  };

  return (
    <div className="list-container" style={{ display: "flex", gap: menuVisible ? "40px" : "0px", height: "100%", transition: "gap 0.3s ease" }}>
      {/* Left Sidebar Menu */}
      <aside style={{
        width: menuVisible ? "180px" : "0px",
        minWidth: menuVisible ? "180px" : "0px",
        flexShrink: 0,
        borderRight: menuVisible ? "1px solid rgba(59, 130, 246, 0.15)" : "none",
        paddingRight: menuVisible ? "20px" : "0px",
        overflowY: "auto",
        maxHeight: "100vh",
        overflowX: "hidden",
        transition: "all 0.3s ease",
        opacity: menuVisible ? 1 : 0,
        pointerEvents: menuVisible ? "auto" : "none"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h3 style={{ color: "#5B9EF0", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", margin: "0", letterSpacing: "0.5px" }}>
            Reports
          </h3>
          <button
            onClick={() => setMenuVisible(!menuVisible)}
            style={{
              background: "transparent",
              border: "none",
              color: "#7A9CC4",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "color 0.2s"
            }}
            onMouseEnter={(e) => e.target.style.color = "#5B9EF0"}
            onMouseLeave={(e) => e.target.style.color = "#7A9CC4"}
            title={menuVisible ? "Hide menu" : "Show menu"}
          >
            {SVG_ICONS["chevron-left"]({ size: 16 })}
          </button>
        </div>
        <button
          onClick={() => setActiveReport("content-interaction")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "10px 12px",
            background: activeReport === "content-interaction" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeReport === "content-interaction" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeReport === "content-interaction" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            textAlign: "left",
            transition: "all 0.2s",
            marginBottom: "8px"
          }}
        >
          {SVG_ICONS["chart-bar"]({ size: 16, color: activeReport === "content-interaction" ? "#5B9EF0" : "#7A9CC4" })}
          Content Interaction
        </button>
        <button
          onClick={() => setActiveReport("ori-metrics")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "10px 12px",
            background: activeReport === "ori-metrics" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeReport === "ori-metrics" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeReport === "ori-metrics" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            textAlign: "left",
            transition: "all 0.2s",
            marginBottom: "8px"
          }}
        >
          {SVG_ICONS["message-square"]({ size: 16, color: activeReport === "ori-metrics" ? "#5B9EF0" : "#7A9CC4" })}
          ORI Metrics
        </button>
        <button
          onClick={() => setActiveReport("api-billing")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "10px 12px",
            background: activeReport === "api-billing" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeReport === "api-billing" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeReport === "api-billing" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            textAlign: "left",
            transition: "all 0.2s",
            marginBottom: "8px"
          }}
        >
          {SVG_ICONS["trending-up"]({ size: 16, color: activeReport === "api-billing" ? "#5B9EF0" : "#7A9CC4" })}
          API Billing and Usage
        </button>
        <button
          onClick={() => setActiveReport("sharing")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "10px 12px",
            background: activeReport === "sharing" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeReport === "sharing" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeReport === "sharing" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            textAlign: "left",
            transition: "all 0.2s"
          }}
        >
          {SVG_ICONS["link"]({ size: 16, color: activeReport === "sharing" ? "#5B9EF0" : "#7A9CC4" })}
          Sharing Report
        </button>
        <button
          onClick={() => setActiveReport("consultation")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "10px 12px",
            background: activeReport === "consultation" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeReport === "consultation" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeReport === "consultation" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            textAlign: "left",
            transition: "all 0.2s"
          }}
        >
          {SVG_ICONS["phone"]({ size: 16, color: activeReport === "consultation" ? "#5B9EF0" : "#7A9CC4" })}
          Consultation Activity
        </button>
      </aside>

      {/* Content Area */}
      <div style={{ flex: 1, overflowY: "auto", maxHeight: "100vh", paddingRight: menuVisible ? "20px" : "40px", display: "flex", flexDirection: "column", transition: "padding 0.3s ease" }}>
        {!menuVisible && (
          <button
            onClick={() => setMenuVisible(!menuVisible)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              background: "transparent",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              color: "#7A9CC4",
              borderRadius: "6px",
              cursor: "pointer",
              padding: "8px 12px",
              fontSize: "12px",
              fontWeight: "600",
              transition: "all 0.2s",
              alignSelf: "flex-start",
              marginBottom: "12px"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(59, 130, 246, 0.15)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
            }}
            title="Show menu"
          >
            ☰ Menu
          </button>
        )}

        {activeReport === "content-interaction" && (
          <div>
            <h2 style={{ margin: "0 0 24px 0", color: "#5B9EF0" }}>Content Interaction</h2>

            {/* Time Range Buttons */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
              {[7, 30, 60, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setTimeRange(days)}
                  style={{
                    padding: "8px 16px",
                    background: timeRange === days ? "rgba(59, 130, 246, 0.2)" : "transparent",
                    border: timeRange === days ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                    color: timeRange === days ? "#5B9EF0" : "#7A9CC4",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.2s"
                  }}
                >
                  {days} Days
                </button>
              ))}
            </div>

            {metrics.loading ? (
              <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px" }}>Loading metrics...</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}>
                {/* News Clicks */}
                <div style={{
                  padding: "24px",
                  background: "rgba(245, 158, 11, 0.08)",
                  border: "1px solid rgba(245, 158, 11, 0.2)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>News Clicks</h3>
                  <div style={{ color: "#F59E0B", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{metrics.newsClicks}</div>
                  <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>News article clicks</p>
                </div>

                {/* Resource Clicks */}
                <div style={{
                  padding: "24px",
                  background: "rgba(245, 158, 11, 0.08)",
                  border: "1px solid rgba(245, 158, 11, 0.2)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Resource Clicks</h3>
                  <div style={{ color: "#F59E0B", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{metrics.resourceClicks}</div>
                  <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Resource/link clicks</p>
                </div>

                {/* Lessons Completed */}
                <div style={{
                  padding: "24px",
                  background: "rgba(52, 211, 153, 0.08)",
                  border: "1px solid rgba(52, 211, 153, 0.2)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Lessons Completed</h3>
                  <div style={{ color: "#34D399", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{metrics.lessonsCompleted}</div>
                  <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Total completions</p>
                </div>

                {/* Prompts Copied */}
                <div style={{
                  padding: "24px",
                  background: "rgba(168, 85, 247, 0.08)",
                  border: "1px solid rgba(168, 85, 247, 0.2)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Prompts Copied</h3>
                  <div style={{ color: "#A855F7", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{metrics.promptsCopied}</div>
                  <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Total copies</p>
                </div>

                {/* Assessments Taken */}
                <div style={{
                  padding: "24px",
                  background: "rgba(59, 130, 246, 0.08)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Assessments Taken</h3>
                  <div style={{ color: "#3B82F6", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{metrics.assessmentsTaken}</div>
                  <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Total assessments</p>
                </div>

                {/* ORI Usage */}
                <div style={{
                  padding: "24px",
                  background: "rgba(236, 72, 153, 0.08)",
                  border: "1px solid rgba(236, 72, 153, 0.2)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>ORI Usage</h3>
                  <div style={{ color: "#EC4899", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{metrics.oriUsage}</div>
                  <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Total ORI interactions</p>
                </div>
              </div>
            )}

            {/* Stacked Bar Chart */}
            {!metrics.loading && (
              <div style={{ marginTop: "40px", padding: "20px", background: "rgba(59, 130, 246, 0.05)", borderRadius: "8px", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h3 style={{ color: "#5B9EF0", margin: "0", fontSize: "16px", fontWeight: "600" }}>Interactions Over Time</h3>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => loadMetrics()}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                        borderRadius: "6px",
                        color: "#5B9EF0",
                        cursor: "pointer",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.1)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                      }}
                      title="Refresh data"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                      </svg>
                      Refresh
                    </button>
                    <button
                      onClick={() => setChartExpanded(!chartExpanded)}
                    style={{
                      background: "transparent",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      borderRadius: "6px",
                      color: "#5B9EF0",
                      cursor: "pointer",
                      padding: "8px 12px",
                      fontSize: "12px",
                      fontWeight: "600",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                    }}
                  >
                    {chartExpanded ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                        </svg>
                        Collapse
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3v6a2 2 0 0 0 2 2h6V3M3 8h6a2 2 0 0 1 2 2v6h6V8a2 2 0 0 0-2-2h-6V3m0 18v-6a2 2 0 0 1 2-2h6v6h6v6a2 2 0 0 1-2 2h-6v-6h-6z" />
                        </svg>
                        Expand
                      </>
                    )}
                    </button>
                  </div>
                </div>
                {chartData && chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartExpanded ? 600 : 300}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.1)" />
                    <XAxis
                      dataKey="date"
                      stroke="#7A9CC4"
                      style={{ fontSize: "12px" }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis stroke="#7A9CC4" style={{ fontSize: "12px" }} />
                    <Tooltip
                      contentStyle={{ background: "#0a1628", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "6px", color: "#fff" }}
                      cursor={{ fill: "rgba(59, 130, 246, 0.05)" }}
                    />
                    <Legend />
                    <Bar dataKey="news" stackId="a" fill="#F59E0B" name="News Clicks" />
                    <Bar dataKey="resources" stackId="a" fill="#FBBF24" name="Resource Clicks" />
                    <Bar dataKey="lessons" stackId="a" fill="#34D399" name="Lessons Completed" />
                    <Bar dataKey="prompts" stackId="a" fill="#A855F7" name="Prompts Copied" />
                    <Bar dataKey="assessments" stackId="a" fill="#3B82F6" name="Assessments Taken" />
                    <Bar dataKey="ori" stackId="a" fill="#EC4899" name="ORI Usage" />
                  </BarChart>
                </ResponsiveContainer>
                ) : (
                  <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px", fontSize: "14px" }}>Loading chart data...</div>
                )}
              </div>
            )}

            {/* Expanded Chart Modal */}
            {chartExpanded && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10000,
                padding: "20px"
              }}>
                <div style={{
                  background: "#0a1628",
                  borderRadius: "12px",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  width: "100%",
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                    borderBottom: "1px solid rgba(59, 130, 246, 0.2)"
                  }}>
                    <h2 style={{ color: "#5B9EF0", margin: "0", fontSize: "18px", fontWeight: "600" }}>Interactions Over Time</h2>
                    <button
                      onClick={() => setChartExpanded(false)}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                        borderRadius: "6px",
                        color: "#5B9EF0",
                        cursor: "pointer",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.1)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <div style={{ flex: 1, padding: "20px", overflow: "auto" }}>
                    {chartData && chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={500}>
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.1)" />
                          <XAxis
                            dataKey="date"
                            stroke="#7A9CC4"
                            style={{ fontSize: "12px" }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis stroke="#7A9CC4" style={{ fontSize: "12px" }} />
                          <Tooltip
                            contentStyle={{ background: "#0a1628", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "6px", color: "#fff" }}
                          />
                          <Legend />
                          <Bar dataKey="news" stackId="a" fill="#F59E0B" name="News Clicks" />
                          <Bar dataKey="resources" stackId="a" fill="#FBBF24" name="Resource Clicks" />
                          <Bar dataKey="lessons" stackId="a" fill="#34D399" name="Lessons Completed" />
                          <Bar dataKey="prompts" stackId="a" fill="#A855F7" name="Prompts Copied" />
                          <Bar dataKey="assessments" stackId="a" fill="#3B82F6" name="Assessments Taken" />
                          <Bar dataKey="ori" stackId="a" fill="#EC4899" name="ORI Usage" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px" }}>No chart data</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Top 3 Items by Type */}
            {!metrics.loading && (
              <div style={{ marginTop: "40px" }}>
                <h3 style={{ color: "#5B9EF0", margin: "0 0 24px 0", fontSize: "16px", fontWeight: "600" }}>Top 3 by Type</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}>
                  {/* Top News */}
                  <div style={{ padding: "20px", background: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.2)", borderRadius: "8px" }}>
                    <h4 style={{ color: "#F59E0B", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Top News Clicked</h4>
                    {topItems.news.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {topItems.news.map((item, idx) => (
                          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ color: "#F59E0B", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.title || "Untitled"}</div>
                              <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} clicks</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No data</div>
                    )}
                  </div>

                  {/* Top Resources */}
                  <div style={{ padding: "20px", background: "rgba(251, 191, 36, 0.08)", border: "1px solid rgba(251, 191, 36, 0.2)", borderRadius: "8px" }}>
                    <h4 style={{ color: "#FBBF24", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Top Resources Clicked</h4>
                    {topItems.resources.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {topItems.resources.map((item, idx) => (
                          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ color: "#FBBF24", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.title || "Untitled"}</div>
                              <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} clicks</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No data</div>
                    )}
                  </div>

                  {/* Top Lessons */}
                  <div style={{ padding: "20px", background: "rgba(52, 211, 153, 0.08)", border: "1px solid rgba(52, 211, 153, 0.2)", borderRadius: "8px" }}>
                    <h4 style={{ color: "#34D399", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Top Lessons Completed</h4>
                    {topItems.lessons.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {topItems.lessons.map((item, idx) => (
                          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ color: "#34D399", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.title || "Untitled"}</div>
                              <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} completions</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No data</div>
                    )}
                  </div>

                  {/* Top Lessons */}
                  <div style={{ padding: "20px", background: "rgba(52, 211, 153, 0.08)", border: "1px solid rgba(52, 211, 153, 0.2)", borderRadius: "8px" }}>
                    <h4 style={{ color: "#34D399", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Top Lessons Completed</h4>
                    {topItems.lessons.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {topItems.lessons.map((item, idx) => (
                          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ color: "#34D399", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.title || "Untitled"}</div>
                              <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} completions</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No data</div>
                    )}
                  </div>

                  {/* Top Prompts */}
                  <div style={{ padding: "20px", background: "rgba(168, 85, 247, 0.08)", border: "1px solid rgba(168, 85, 247, 0.2)", borderRadius: "8px" }}>
                    <h4 style={{ color: "#A855F7", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Top Prompts Copied</h4>
                    {topItems.prompts.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {topItems.prompts.map((item, idx) => (
                          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ color: "#A855F7", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.title || "Untitled"}</div>
                              <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} copies</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No data</div>
                    )}
                  </div>

                  {/* Top Assessments */}
                  <div style={{ padding: "20px", background: "rgba(59, 130, 246, 0.08)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px" }}>
                    <h4 style={{ color: "#3B82F6", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Top Assessments Taken</h4>
                    {topItems.assessments.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {topItems.assessments.map((item, idx) => (
                          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ color: "#3B82F6", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.title || "Untitled"}</div>
                              <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} taken</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No data</div>
                    )}
                  </div>

                </div>
              </div>
              )}

              {/* Top Categories by Type */}
              {!metrics.loading && (
                <div style={{ marginTop: "40px" }}>
                  <h3 style={{ color: "#5B9EF0", margin: "0 0 24px 0", fontSize: "16px", fontWeight: "600" }}>Top Categories by Type</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}>
                    {/* Top Resources Categories */}
                    <div style={{ padding: "20px", background: "rgba(251, 191, 36, 0.08)", border: "1px solid rgba(251, 191, 36, 0.2)", borderRadius: "8px" }}>
                      <h4 style={{ color: "#FBBF24", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Resources Categories</h4>
                      {topCategories.resources.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {topCategories.resources.map((item, idx) => (
                            <div key={item.category} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <span style={{ color: "#FBBF24", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.category || "Uncategorized"}</div>
                                <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} items</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No categories</div>
                      )}
                    </div>

                    {/* Top Lessons Categories */}
                    <div style={{ padding: "20px", background: "rgba(52, 211, 153, 0.08)", border: "1px solid rgba(52, 211, 153, 0.2)", borderRadius: "8px" }}>
                      <h4 style={{ color: "#34D399", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Lessons Categories</h4>
                      {topCategories.lessons.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {topCategories.lessons.map((item, idx) => (
                            <div key={item.category} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <span style={{ color: "#34D399", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.category || "Uncategorized"}</div>
                                <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} items</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No categories</div>
                      )}
                    </div>

                    {/* Top Prompts Categories */}
                    <div style={{ padding: "20px", background: "rgba(168, 85, 247, 0.08)", border: "1px solid rgba(168, 85, 247, 0.2)", borderRadius: "8px" }}>
                      <h4 style={{ color: "#A855F7", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Prompts Categories</h4>
                      {topCategories.prompts.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {topCategories.prompts.map((item, idx) => (
                            <div key={item.category} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <span style={{ color: "#A855F7", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.category || "Uncategorized"}</div>
                                <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} items</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No categories</div>
                      )}
                    </div>

                    {/* Top ORI Topics */}
                    <div style={{ padding: "20px", background: "rgba(236, 72, 153, 0.08)", border: "1px solid rgba(236, 72, 153, 0.2)", borderRadius: "8px" }}>
                      <h4 style={{ color: "#EC4899", margin: "0 0 16px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Top ORI Topics</h4>
                      {topItems.ori.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {topItems.ori.map((item, idx) => (
                            <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <span style={{ color: "#EC4899", fontWeight: "700", fontSize: "16px", minWidth: "20px" }}>{idx + 1}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.title || "Unknown"}</div>
                                <div style={{ color: "#9DB4D4", fontSize: "12px" }}>{item.count} uses</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: "#7A9CC4", fontSize: "13px" }}>No data</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            )}
          </div>
        )}

        {activeReport === "ori-metrics" && (
          <div>
            <h2 style={{ margin: "0 0 24px 0", color: "#5B9EF0" }}>ORI Feedback Metrics</h2>

            {/* Time Range Buttons */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
              {[7, 30, 60, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setTimeRange(days)}
                  style={{
                    padding: "8px 16px",
                    background: timeRange === days ? "rgba(59, 130, 246, 0.2)" : "transparent",
                    border: timeRange === days ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                    color: timeRange === days ? "#5B9EF0" : "#7A9CC4",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.2s"
                  }}
                >
                  {days} Days
                </button>
              ))}
            </div>

            <OriMetricsReport timeRange={timeRange} />
          </div>
        )}

        {activeReport === "api-billing" && (
          <div>
            <h2 style={{ margin: "0 0 24px 0", color: "#5B9EF0" }}>API Billing & Usage</h2>
            <BillingReport />
          </div>
        )}
        {activeReport === "sharing" && (
          <div>
            <h2 style={{ margin: "0 0 24px 0", color: "#5B9EF0" }}>Sharing Report</h2>
            <SharingReport />
          </div>
        )}
        {activeReport === "consultation" && (
          <ConsultationActivityReport ref={ref} />
        )}
      </div>
    </div>
  );
});

export default ReportsTab;
